import { getPlatformDisplayName } from 'src/app/common/platformUtils';
import { Platform, ResourceType } from 'src/app/models/api';
import { TriggerTypes } from 'src/app/models/api/playbooks/triggers/triggerTypes.enum';

export function isCreatedBySupported(platform: Platform, resourceTypes?: Set<ResourceType>) {
  return (
    (resourceTypes && platform == Platform.powerplatform && !resourceTypes.has(ResourceType.connector)) ||
    platform == Platform.powerBI
  );
}

const violationEmailTemplate = {
  subject: 'New Violation found - %VIOLATION_NAME%',
  body: (platform: Platform) => {
    const platformDisplayName = getPlatformDisplayName(platform);
    return `Hi,

This is an automated email notifying you about a security violation recently found in ${platformDisplayName}.

* The Violation Impacts "%RESOURCE_NAME%" which is a %RESOURCE_TYPE% in ${platformDisplayName}.
* The security risk has been rated as %VIOLATION_SEVERITY% risk and was first seen at %VIOLATION_FIRST_SEEN%.

Why is this a risk?
%RISK%

How to triage?
%TRIAGE%

How to fix?
%HOW_TO_FIX%

Helpful links:
* Violation on Zenity: %LINK_TO_ZENITY%

* ${platformDisplayName} %RESOURCE_TYPE% resource: %LINK_TO_PLATFORM%.

Please act swiftly to mitigate the violation found and remove the risk.
`;
  },
};

const resourceEmailTemplate = {
  subject: 'New resource found - %RESOURCE_NAME%',
  body: (platform: Platform, resourceTypes?: Set<ResourceType>) => {
    const creatorDetails = isCreatedBySupported(platform, resourceTypes) ? '* It was created by %CREATED_BY%' : '';
    return `This is an automated email notifying you about a new %RESOURCE_CATEGORY% found.

Here is more information about the resource:

* %RESOURCE_NAME% is a %RESOURCE_TYPE% in ${platform}.
* It was created on %CREATED_AT% and was first seen on %RESOURCE_FIRST_SEEN_AT%
${creatorDetails}
* The resource was created in %ENVIRONMENT_TYPE% %ENVIRONMENT_NAME%.

To get more information about the Resource in Zenity, go to  %INVENTORY_RESOURCE_LINK%

To view the resource in the source platform, go to %LINK_TO_PLATFORM%`;
  },
};

const findingEmailTemplate = {
  subject: 'New Runtime Finding detected: %FINDING_RULE_NAME%',
  body: (platform: Platform, resourceTypes?: Set<ResourceType>) => {
    return `Hello,
New AI runtime finding detected. Please review and take appropriate action based on the severity and recommendations below.

Finding type:         %FINDING_RULE_NAME%
Severity:             %FINDING_SEVERITY%
Detected at:          %FINDING_TIMESTAMP%
AI Service:           %AI_SERVICE%
Agent Name:           %AGENT_NAME%
Actor email address:  %ACTOR_EMAIL%


HOW TO INVESTIGATE
──────────────────
%FINDING_RULE_HOW_TO_INVESTIGATE%


RESPONSE RECOMMENDATION
───────────────────────
%FINDING_RULE_RESPONSE_RECOMMENDATION%


To view the complete finding details in Zenity, please visit:
%LINK_TO_ZENITY%

Best regards,
Zenity Security Team
`;
  },
};

const issueEmailTemplate = {
  subject: 'New Issue detected: %ISSUE_NAME%',
  body: (platform: Platform, resourceTypes?: Set<ResourceType>) => {
    return `Hello,
A new security issue has been detected. Please review and take appropriate action based on the severity and details below.

Issue:                %ISSUE_NAME%
Severity:             %ISSUE_SEVERITY%
Taxonomy:             %ISSUE_TAXONOMY%
Rule ID:              %ISSUE_RULE_ID%
Resource:             %ISSUE_RESOURCE_NAME%
Actor:                %ACTOR_NAME%
Actor email address:  %ACTOR_EMAIL%

DESCRIPTION
───────────
%ISSUE_DESCRIPTION%

Please act swiftly to investigate and resolve this issue.

Best regards,
Zenity Security Team
`;
  },
};

export const emailTemplatesByTriggerType: {
  [key in TriggerTypes]: {
    subject: string;
    body: (platform: Platform, resourceTypes?: Set<ResourceType>) => string;
  };
} = {
  [TriggerTypes.newViolationFound]: violationEmailTemplate,
  [TriggerTypes.newResourceFound]: resourceEmailTemplate,
  [TriggerTypes.newOrExistingViolationFound]: violationEmailTemplate,
  [TriggerTypes.newOrExistingResourceFound]: resourceEmailTemplate,
  [TriggerTypes.existingViolationFound]: violationEmailTemplate,
  [TriggerTypes.existingResourceFound]: resourceEmailTemplate,
  [TriggerTypes.newFindingFound]: findingEmailTemplate,
  [TriggerTypes.newIssueFound]: issueEmailTemplate,
};
